import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatCurrency, formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import PlanMarketingDialog from './PlanMarketingDialog';
import { marketingProblems, toForm } from './marketing';
import {
  SUBSCRIPTION_STATUSES,
  type Plan,
  type Subscription,
  type TenantRef,
  apiError,
  limitLabel,
} from '../lib';

interface Usage {
  plan: Plan | null;
  usage: Record<'branches' | 'users' | 'devices', { used: number; limit: number | null }>;
}

const BLANK_PLAN = {
  code: '',
  name: '',
  description: '',
  priceMonthly: '0',
  currency: 'SAR',
  maxBranches: '',
  maxUsers: '',
  maxDevices: '',
  isActive: true,
};

/** Plans and the tenant subscription lifecycle. */
export default function Plans() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canManage = can('platform.manage_plans');

  const [plans, setPlans] = useState<Plan[]>([]);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [tenants, setTenants] = useState<TenantRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const [planOpen, setPlanOpen] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [planForm, setPlanForm] = useState(BLANK_PLAN);
  const [saving, setSaving] = useState(false);

  const [trialOpen, setTrialOpen] = useState(false);
  const [trial, setTrial] = useState({ tenantId: '', planId: '', trialDays: '14' });

  const [cancelling, setCancelling] = useState<Subscription | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [activating, setActivating] = useState<Subscription | null>(null);
  const [activatePlanId, setActivatePlanId] = useState('');
  const [activateEndsAt, setActivateEndsAt] = useState('');
  const [usageFor, setUsageFor] = useState<{ tenant: TenantRef; usage: Usage } | null>(null);
  /* The plan whose public card copy is being written, if any. */
  const [marketingFor, setMarketingFor] = useState<Plan | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<{ data: Plan[] }>('/subscriptions/plans', { params: { limit: 100 } }),
      api.get<{ data: Subscription[] }>('/subscriptions', {
        params: { limit: 100, status: statusFilter || undefined },
      }),
    ])
      .then(([planRes, subRes]) => {
        setPlans(planRes.data.data ?? []);
        setSubs(subRes.data.data ?? []);
      })
      .catch((e) => setError(apiError(e, t('platform.plans.loadFailed'))))
      .finally(() => setLoading(false));
  }, [statusFilter, t]);

  useEffect(load, [load]);

  useEffect(() => {
    api
      .get<{ data: TenantRef[] }>('/tenants', { params: { limit: 200 } })
      .then(({ data }) => setTenants(data.data ?? []))
      .catch(() => setTenants([]));
  }, []);

  const openCreatePlan = () => {
    setEditing(null);
    setPlanForm(BLANK_PLAN);
    setPlanOpen(true);
  };

  const openEditPlan = (plan: Plan) => {
    setEditing(plan);
    setPlanForm({
      code: plan.code,
      name: plan.name,
      description: plan.description ?? '',
      priceMonthly: plan.priceMonthly,
      currency: plan.currency,
      maxBranches: plan.maxBranches == null ? '' : String(plan.maxBranches),
      maxUsers: plan.maxUsers == null ? '' : String(plan.maxUsers),
      maxDevices: plan.maxDevices == null ? '' : String(plan.maxDevices),
      isActive: plan.isActive,
    });
    setPlanOpen(true);
  };

  const submitPlan = async () => {
    setSaving(true);
    // An empty ceiling means unlimited, so it is omitted rather than sent as 0.
    const body = {
      name: planForm.name,
      description: planForm.description || undefined,
      priceMonthly: Number(planForm.priceMonthly) || 0,
      currency: planForm.currency,
      maxBranches: planForm.maxBranches ? Number(planForm.maxBranches) : undefined,
      maxUsers: planForm.maxUsers ? Number(planForm.maxUsers) : undefined,
      maxDevices: planForm.maxDevices ? Number(planForm.maxDevices) : undefined,
      isActive: planForm.isActive,
    };
    try {
      if (editing) await api.patch(`/subscriptions/plans/${editing.id}`, body);
      else await api.post('/subscriptions/plans', { ...body, code: planForm.code });
      toast.success(t(editing ? 'platform.plans.updated' : 'platform.plans.created'));
      setPlanOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.plans.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const retirePlan = async (plan: Plan) => {
    if (!window.confirm(t('platform.plans.retireConfirm', { name: plan.name }))) return;
    try {
      await api.delete(`/subscriptions/plans/${plan.id}`);
      toast.success(t('platform.plans.retired'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.plans.saveFailed')));
    }
  };

  const startTrial = async () => {
    try {
      await api.post(`/subscriptions/tenants/${trial.tenantId}/trial`, {
        planId: trial.planId,
        trialDays: Number(trial.trialDays) || 14,
      });
      toast.success(t('platform.subscriptions.trialStarted'));
      setTrialOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.subscriptions.failed')));
    }
  };

  const lifecycle = async (subscription: Subscription, action: 'past-due' | 'expire') => {
    try {
      await api.post(`/subscriptions/${subscription.id}/${action}`, {});
      toast.success(t('platform.subscriptions.updated'));
      load();
    } catch (e) {
      // The service asserts the transition, so an illegal move says so.
      toast.error(apiError(e, t('platform.subscriptions.failed')));
    }
  };

  const activate = async () => {
    if (!activating) return;
    try {
      await api.post(`/subscriptions/${activating.id}/activate`, {
        planId: activatePlanId || undefined,
        endsAt: activateEndsAt ? new Date(activateEndsAt).toISOString() : undefined,
      });
      toast.success(t('platform.subscriptions.activated'));
      setActivating(null);
      setActivatePlanId('');
      setActivateEndsAt('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.subscriptions.failed')));
    }
  };

  const cancel = async () => {
    if (!cancelling) return;
    try {
      await api.post(`/subscriptions/${cancelling.id}/cancel`, {
        reason: cancelReason || undefined,
      });
      toast.success(t('platform.subscriptions.cancelled'));
      setCancelling(null);
      setCancelReason('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.subscriptions.failed')));
    }
  };

  const showUsage = async (subscription: Subscription) => {
    const tenant =
      subscription.tenant ?? tenants.find((candidate) => candidate.id === subscription.tenantId);
    if (!tenant) return;
    try {
      const { data } = await api.get<Usage>(`/subscriptions/tenants/${tenant.id}/usage`);
      setUsageFor({ tenant, usage: data });
    } catch (e) {
      toast.error(apiError(e, t('platform.subscriptions.failed')));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{t('nav.plans')}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{t('platform.plans.subtitle')}</p>
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2">
              <CardTitle>{t('platform.plans.title')}</CardTitle>
              {canManage && <Button onClick={openCreatePlan}>{t('platform.plans.newPlan')}</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {plans.length === 0 ? (
                <EmptyState title={t('platform.plans.empty')} hint={t('platform.plans.emptyHint')} />
              ) : (
                <TableWrap className="rounded-none border-x-0 border-t-0">
                  <Table>
                    <THead>
                      <tr>
                        <TH>{t('platform.plans.plan')}</TH>
                        <TH>{t('platform.plans.price')}</TH>
                        <TH>{t('platform.plans.limits')}</TH>
                        <TH>{t('platform.plans.subscribers')}</TH>
                        <TH>{t('platform.plans.marketing.column')}</TH>
                        <TH>{t('platform.plans.state')}</TH>
                        <TH className="text-end">{t('platform.actions')}</TH>
                      </tr>
                    </THead>
                    <tbody>
                      {plans.map((plan) => (
                        <TR key={plan.id}>
                          <TD>
                            <div className="font-medium">{plan.name}</div>
                            <code className="text-xs text-[var(--muted-foreground)]">{plan.code}</code>
                          </TD>
                          <TD>
                            {formatCurrency(plan.priceMonthly, plan.currency, i18n.language)}
                            <div className="text-xs text-[var(--muted-foreground)]">
                              {t('platform.plans.perMonth')}
                            </div>
                          </TD>
                          <TD className="text-xs">
                            <div>
                              {t('platform.plans.branches')}:{' '}
                              {limitLabel(plan.maxBranches, t('platform.plans.unlimited'))}
                            </div>
                            <div>
                              {t('platform.plans.users')}:{' '}
                              {limitLabel(plan.maxUsers, t('platform.plans.unlimited'))}
                            </div>
                            <div>
                              {t('platform.plans.devices')}:{' '}
                              {limitLabel(plan.maxDevices, t('platform.plans.unlimited'))}
                            </div>
                          </TD>
                          <TD>{plan._count?.subscriptions ?? 0}</TD>
                        <TD>
                          {/*
                            Whether this plan appears on tarabeeza.com at all.
                            A plan with no marketing copy, or with only half of
                            it, is left off the public band rather than drawn
                            with an empty body — so the honest label here is
                            "not on the site", not "incomplete".
                          */}
                          {marketingProblems(toForm(plan.marketing), plan.priceMonthly).length ===
                          0 ? (
                            <Badge tone="success">{t('platform.plans.marketing.onSite')}</Badge>
                          ) : (
                            <Badge tone="neutral">{t('platform.plans.marketing.offSite')}</Badge>
                          )}
                        </TD>
                          <TD>
                            <Badge tone={plan.isActive ? 'success' : 'neutral'}>
                              {t(plan.isActive ? 'platform.plans.sellable' : 'platform.plans.retiredState')}
                            </Badge>
                          </TD>
                          <TD className="text-end">
                            {canManage && (
                              <div className="flex flex-wrap justify-end gap-2">
                                <Button size="sm" variant="secondary" onClick={() => openEditPlan(plan)}>
                                  {t('common.edit')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setMarketingFor(plan)}
                                >
                                  {t('platform.plans.marketing.edit')}
                                </Button>
                                {plan.isActive && (
                                  <Button size="sm" variant="danger" onClick={() => retirePlan(plan)}>
                                    {t('platform.plans.retire')}
                                  </Button>
                                )}
                              </div>
                            )}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>{t('platform.subscriptions.title')}</CardTitle>
              <div className="flex items-center gap-2">
                <Select
                  className="w-44"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label={t('platform.subscriptions.status')}
                >
                  <option value="">{t('platform.subscriptions.allStatuses')}</option>
                  {SUBSCRIPTION_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {t(`platform.subscriptionStatus.${value}`)}
                    </option>
                  ))}
                </Select>
                {canManage && (
                  <Button onClick={() => setTrialOpen(true)}>
                    {t('platform.subscriptions.startTrial')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {subs.length === 0 ? (
                <EmptyState
                  title={t('platform.subscriptions.empty')}
                  hint={t('platform.subscriptions.emptyHint')}
                />
              ) : (
                <TableWrap className="rounded-none border-x-0 border-t-0">
                  <Table>
                    <THead>
                      <tr>
                        <TH>{t('platform.subscriptions.tenant')}</TH>
                        <TH>{t('platform.subscriptions.plan')}</TH>
                        <TH>{t('platform.subscriptions.status')}</TH>
                        <TH>{t('platform.subscriptions.term')}</TH>
                        <TH className="text-end">{t('platform.actions')}</TH>
                      </tr>
                    </THead>
                    <tbody>
                      {subs.map((subscription) => (
                        <TR key={subscription.id}>
                          <TD>
                            <div className="font-medium">{subscription.tenant?.name ?? '—'}</div>
                            <code className="text-xs text-[var(--muted-foreground)]">
                              {subscription.tenant?.slug ?? subscription.tenantId}
                            </code>
                          </TD>
                          <TD>{subscription.plan?.name ?? subscription.planId}</TD>
                          <TD>
                            <Badge tone={toneForStatus(subscription.status)}>
                              {t(`platform.subscriptionStatus.${subscription.status}`)}
                            </Badge>
                          </TD>
                          <TD className="text-xs text-[var(--muted-foreground)]">
                            <div>
                              {t('platform.subscriptions.startsAt')}:{' '}
                              {formatDateTime(subscription.startsAt, i18n.language)}
                            </div>
                            {subscription.trialEndsAt && (
                              <div>
                                {t('platform.subscriptions.trialEndsAt')}:{' '}
                                {formatDateTime(subscription.trialEndsAt, i18n.language)}
                              </div>
                            )}
                            {subscription.endsAt && (
                              <div>
                                {t('platform.subscriptions.endsAt')}:{' '}
                                {formatDateTime(subscription.endsAt, i18n.language)}
                              </div>
                            )}
                          </TD>
                          <TD className="text-end">
                            <div className="flex flex-wrap justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => showUsage(subscription)}>
                                {t('platform.subscriptions.usage')}
                              </Button>
                              {canManage && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setActivating(subscription);
                                      setActivatePlanId('');
                                      setActivateEndsAt('');
                                    }}
                                  >
                                    {t('platform.subscriptions.activate')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => lifecycle(subscription, 'past-due')}
                                  >
                                    {t('platform.subscriptions.pastDue')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => lifecycle(subscription, 'expire')}
                                  >
                                    {t('platform.subscriptions.expire')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => setCancelling(subscription)}
                                  >
                                    {t('platform.subscriptions.cancel')}
                                  </Button>
                                </>
                              )}
                            </div>
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <PlanMarketingDialog
        plan={marketingFor}
        onClose={() => setMarketingFor(null)}
        onSaved={load}
      />

      <Dialog
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        title={t(editing ? 'platform.plans.editPlan' : 'platform.plans.newPlan')}
        description={t('platform.plans.formHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={submitPlan}
              disabled={saving || !planForm.name.trim() || (!editing && !planForm.code.trim())}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/*
            The code is shown when editing too, disabled.

            It used to be hidden entirely once a plan existed, on the reasoning
            that it cannot be changed. But the code is how two plans are told
            apart when their NAMES do not tell them apart — a venue ended up
            with a plan called "Business" whose code was ENTERPRISE sitting
            beside one called "Enterprise" whose code was enterprise, and the
            edit dialog showed neither, so there was no way to know which row
            was open. Unchangeable is a reason to disable a field, not to hide
            the only thing identifying the record.
          */}
          <Field
            label={t('platform.plans.code')}
            required={!editing}
            hint={editing ? t('platform.plans.codeFixed') : t('platform.plans.codeHint')}
          >
            <Input
              value={planForm.code}
              disabled={Boolean(editing)}
              onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })}
            />
          </Field>
          <Field label={t('platform.plans.name')} required>
            <Input
              value={planForm.name}
              onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
            />
          </Field>
          <Field label={t('platform.plans.description')}>
            <Textarea
              rows={2}
              value={planForm.description}
              onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('platform.plans.priceMonthly')}>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={planForm.priceMonthly}
                onChange={(e) => setPlanForm({ ...planForm, priceMonthly: e.target.value })}
              />
            </Field>
            <Field label={t('platform.plans.currency')}>
              <Input
                maxLength={3}
                value={planForm.currency}
                onChange={(e) => setPlanForm({ ...planForm, currency: e.target.value.toUpperCase() })}
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t('platform.plans.branches')} hint={t('platform.plans.blankUnlimited')}>
              <Input
                type="number"
                min="1"
                value={planForm.maxBranches}
                onChange={(e) => setPlanForm({ ...planForm, maxBranches: e.target.value })}
              />
            </Field>
            <Field label={t('platform.plans.users')} hint={t('platform.plans.blankUnlimited')}>
              <Input
                type="number"
                min="1"
                value={planForm.maxUsers}
                onChange={(e) => setPlanForm({ ...planForm, maxUsers: e.target.value })}
              />
            </Field>
            <Field label={t('platform.plans.devices')} hint={t('platform.plans.blankUnlimited')}>
              <Input
                type="number"
                min="1"
                value={planForm.maxDevices}
                onChange={(e) => setPlanForm({ ...planForm, maxDevices: e.target.value })}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={planForm.isActive}
              onChange={(e) => setPlanForm({ ...planForm, isActive: e.target.checked })}
            />
            {t('platform.plans.sellable')}
          </label>
        </div>
      </Dialog>

      <Dialog
        open={trialOpen}
        onClose={() => setTrialOpen(false)}
        title={t('platform.subscriptions.startTrial')}
        description={t('platform.subscriptions.trialHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setTrialOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={startTrial} disabled={!trial.tenantId || !trial.planId}>
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('platform.subscriptions.tenant')} required>
            <Select
              value={trial.tenantId}
              onChange={(e) => setTrial({ ...trial, tenantId: e.target.value })}
            >
              <option value="">{t('platform.subscriptions.chooseTenant')}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('platform.subscriptions.plan')} required>
            <Select
              value={trial.planId}
              onChange={(e) => setTrial({ ...trial, planId: e.target.value })}
            >
              <option value="">{t('platform.subscriptions.choosePlan')}</option>
              {plans
                .filter((plan) => plan.isActive)
                .map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label={t('platform.subscriptions.trialDays')}>
            <Input
              type="number"
              min="1"
              max="365"
              value={trial.trialDays}
              onChange={(e) => setTrial({ ...trial, trialDays: e.target.value })}
            />
          </Field>
        </div>
      </Dialog>

      {activating && (
        <Dialog
          open
          onClose={() => setActivating(null)}
          title={t('platform.subscriptions.activate')}
          description={t('platform.subscriptions.activateHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setActivating(null)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={activate}>{t('common.confirm')}</Button>
            </>
          }
        >
          <div className="space-y-4">
            <Field label={t('platform.subscriptions.movePlan')} hint={t('platform.subscriptions.movePlanHint')}>
              <Select value={activatePlanId} onChange={(e) => setActivatePlanId(e.target.value)}>
                <option value="">{t('platform.subscriptions.keepPlan')}</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('platform.subscriptions.paidThrough')} hint={t('platform.subscriptions.paidThroughHint')}>
              <Input
                type="datetime-local"
                value={activateEndsAt}
                onChange={(e) => setActivateEndsAt(e.target.value)}
              />
            </Field>
          </div>
        </Dialog>
      )}

      {cancelling && (
        <Dialog
          open
          onClose={() => setCancelling(null)}
          title={t('platform.subscriptions.cancel')}
          description={t('platform.subscriptions.cancelHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setCancelling(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={cancel}>
                {t('common.confirm')}
              </Button>
            </>
          }
        >
          <Field label={t('platform.subscriptions.reason')}>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          </Field>
        </Dialog>
      )}

      {usageFor && (
        <Dialog
          open
          onClose={() => setUsageFor(null)}
          title={t('platform.subscriptions.usageFor', { name: usageFor.tenant.name })}
          description={
            usageFor.usage.plan
              ? t('platform.subscriptions.againstPlan', { name: usageFor.usage.plan.name })
              : t('platform.subscriptions.noPlan')
          }
          footer={<Button onClick={() => setUsageFor(null)}>{t('common.close')}</Button>}
        >
          <ul className="divide-y rounded-lg border text-sm">
            {(['branches', 'users', 'devices'] as const).map((key) => {
              const row = usageFor.usage.usage[key];
              const atCeiling = row.limit != null && row.used >= row.limit;
              return (
                <li key={key} className="flex items-center justify-between gap-2 p-3">
                  <span>{t(`platform.plans.${key}`)}</span>
                  <span className={atCeiling ? 'font-medium text-red-600' : ''}>
                    {row.used} / {limitLabel(row.limit, t('platform.plans.unlimited'))}
                  </span>
                </li>
              );
            })}
          </ul>
        </Dialog>
      )}
    </div>
  );
}
